import { useTranslation } from "react-i18next";
import { getLocale, translate as tr } from "../../i18n";
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import Sidebar from '../common/sidebar';
import Footer from '../common/footer';
import '../../assets/css/hr-report.css';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatSeconds(seconds) {
  if (!seconds || seconds <= 0) return '0h 0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function toDecimalHours(seconds) {
  return seconds > 0 ? (seconds / 3600).toFixed(2) : '0.00';
}

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

function thirtyDaysAgo() {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().split('T')[0];
}

function downloadCSV(rows, filename) {
  const content = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  downloadFile(url, filename, () => {
    window.setTimeout(() => URL.revokeObjectURL(url), 60000);
  });
}

function downloadFile(url, filename, onClickComplete) {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'download';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  if (onClickComplete) onClickComplete();
}

const HR_REPORT_TABS = ['attendance', 'leave', 'overtime', 'payroll'];

function getTabFromHash(hashValue) {
  const normalizedHash = String(hashValue || '').replace(/^#/, '').toLowerCase();
  return HR_REPORT_TABS.includes(normalizedHash) ? normalizedHash : 'attendance';
}

// ── Tab: Attendance ────────────────────────────────────────────────────────────

function AttendanceTab({ auth }) {
  useTranslation();
  const [from, setFrom] = useState(thirtyDaysAgo());
  const [to, setTo] = useState(todayStr());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(
        `/api/report/hr-attendance?workspace_id=${auth.workspace_id}&from=${from}&to=${to}`,
        { headers: { Authorization: `Bearer ${auth.token}`, 'Content-Type': 'application/json' } }
      );
      setData(res.data.results || []);
    } catch (err) {
      setError(tr("Failed to load attendance report."));
    } finally {
      setLoading(false);
    }
  }, [auth.workspace_id, auth.token, from, to]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = () => {
    const headers = ['Name', 'Email', 'Days Present', 'Days Absent', 'Missing Checkout', 'Total Worked (h)', 'Avg Daily (h)', 'Overtime (h)'].map((label) => tr(label));
    const rows = data.map((r) => [
      `${r.user.first_name} ${r.user.last_name}`,
      r.user.email,
      r.days_present,
      r.days_absent,
      r.days_missing_checkout,
      toDecimalHours(r.total_worked_seconds),
      toDecimalHours(r.avg_daily_seconds),
      toDecimalHours(r.total_overtime_seconds),
    ]);
    downloadCSV([headers, ...rows], `hr-attendance-${from}-${to}.csv`);
  };

  return (
    <div>
      <div className="hr-toolbar">
        <label>{tr("From")}</label>
        <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        <label>{tr("To")}</label>
        <input type="date" value={to} min={from} max={todayStr()} onChange={(e) => setTo(e.target.value)} />
        <button className="hr-export-btn" onClick={handleExport} disabled={data.length === 0}>{tr("Export CSV")}</button>
      </div>

      {error && <div className="hr-error">{error}</div>}

      <div className="hr-table-wrap">
        {loading ? (
          <div className="hr-loading">{tr("Loading...")}</div>
        ) : data.length === 0 ? (
          <div className="hr-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5"><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"/></svg>
            <p>{tr("No attendance data for this period.")}</p>
          </div>
        ) : (
          <table className="hr-table">
            <thead>
              <tr>
                <th>{tr("Employee")}</th>
                <th>{tr("Days Present")}</th>
                <th>{tr("Days Absent")}</th>
                <th>{tr("Missing Checkout")}</th>
                <th>{tr("Total Worked")}</th>
                <th>{tr("Avg Daily")}</th>
                <th>{tr("Overtime")}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.user.id}>
                  <td>
                    <div className="hr-emp-name">{row.user.first_name} {row.user.last_name}</div>
                    <div className="hr-emp-email">{row.user.email}</div>
                  </td>
                  <td>{row.days_present}</td>
                  <td>{row.days_absent}</td>
                  <td>{row.days_missing_checkout}</td>
                  <td>{formatSeconds(row.total_worked_seconds)}</td>
                  <td>{formatSeconds(row.avg_daily_seconds)}</td>
                  <td>{formatSeconds(row.total_overtime_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Tab: Leave ─────────────────────────────────────────────────────────────────

function LeaveTab({ auth }) {
  useTranslation();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const yearOptions = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(
        `/api/report/hr-leave?workspace_id=${auth.workspace_id}&year=${year}`,
        { headers: { Authorization: `Bearer ${auth.token}`, 'Content-Type': 'application/json' } }
      );
      setData(res.data.results || []);
      setLeaveTypes(res.data.leave_types || []);
    } catch (err) {
      setError(tr("Failed to load leave report."));
    } finally {
      setLoading(false);
    }
  }, [auth.workspace_id, auth.token, year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = () => {
    const typeHeaders = leaveTypes.map((lt) => `${lt.name} Used / Allocated`);
    const headers = [tr('Name'), tr('Email'), ...typeHeaders];
    const rows = data.map((r) => {
      const typeCols = leaveTypes.map((lt) => {
        const bal = r.balances.find((b) => b.leave_type_id === lt.id);
        return bal ? `${bal.used_days} / ${bal.allocated_days}` : '0 / 0';
      });
      return [`${r.user.first_name} ${r.user.last_name}`, r.user.email, ...typeCols];
    });
    downloadCSV([headers, ...rows], `hr-leave-${year}.csv`);
  };

  return (
    <div>
      <div className="hr-toolbar">
        <label>{tr("Year")}</label>
        <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}>
          {yearOptions.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <button className="hr-export-btn" onClick={handleExport} disabled={data.length === 0}>{tr("Export CSV")}</button>
      </div>

      {error && <div className="hr-error">{error}</div>}

      <div className="hr-table-wrap">
        {loading ? (
          <div className="hr-loading">{tr("Loading...")}</div>
        ) : data.length === 0 ? (
          <div className="hr-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            <p>{tr("No leave balance data for")} {year}.</p>
          </div>
        ) : (
          <table className="hr-table">
            <thead>
              <tr>
                <th>{tr("Employee")}</th>
                {leaveTypes.map((lt) => (
                  <th key={lt.id}>
                    <span className="hr-leave-dot" style={{ backgroundColor: lt.color || '#ccc' }} />
                    {lt.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.user.id}>
                  <td>
                    <div className="hr-emp-name">{row.user.first_name} {row.user.last_name}</div>
                    <div className="hr-emp-email">{row.user.email}</div>
                  </td>
                  {leaveTypes.map((lt) => {
                    const bal = row.balances.find((b) => b.leave_type_id === lt.id);
                    const used = bal ? bal.used_days : 0;
                    const alloc = bal ? bal.allocated_days : 0;
                    return (
                      <td key={lt.id}>
                        <span className="hr-leave-dot" style={{ backgroundColor: lt.color || '#ccc' }} />
                        {used} / {alloc}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Tab: Overtime ──────────────────────────────────────────────────────────────

function OvertimeTab({ auth }) {
  useTranslation();
  const [from, setFrom] = useState(thirtyDaysAgo());
  const [to, setTo] = useState(todayStr());
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(
        `/api/report/hr-overtime?workspace_id=${auth.workspace_id}&from=${from}&to=${to}`,
        { headers: { Authorization: `Bearer ${auth.token}`, 'Content-Type': 'application/json' } }
      );
      setData(res.data.results || []);
      setMeta({
        ot_rate_multiplier: res.data.ot_rate_multiplier,
        ot_daily_hours: res.data.ot_daily_hours,
        ot_weekly_hours: res.data.ot_weekly_hours,
      });
    } catch (err) {
      setError(tr("Failed to load overtime report."));
    } finally {
      setLoading(false);
    }
  }, [auth.workspace_id, auth.token, from, to]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleExpand = (uid) => {
    setExpanded((prev) => ({ ...prev, [uid]: !prev[uid] }));
  };

  const handleExport = () => {
    const headers = ['Name', 'Email', 'OT Days', 'Total OT (h)'].map((label) => tr(label));
    const rows = data.map((r) => [
      `${r.user.first_name} ${r.user.last_name}`,
      r.user.email,
      r.ot_days,
      toDecimalHours(r.total_overtime_seconds),
    ]);
    downloadCSV([headers, ...rows], `hr-overtime-${from}-${to}.csv`);
  };

  return (
    <div>
      {(meta.ot_daily_hours != null || meta.ot_weekly_hours != null || meta.ot_rate_multiplier != null) && (
        <div className="hr-ot-info-bar">
          {meta.ot_daily_hours != null && (
            <span>{tr("Daily threshold:")} <strong>{meta.ot_daily_hours}{tr("h")}</strong></span>
          )}
          {meta.ot_weekly_hours != null && (
            <span>{tr("Weekly threshold:")} <strong>{meta.ot_weekly_hours}{tr("h")}</strong></span>
          )}
          {meta.ot_rate_multiplier != null && (
            <span>{tr("OT rate:")} <strong>{meta.ot_rate_multiplier}x</strong></span>
          )}
        </div>
      )}

      <div className="hr-toolbar">
        <label>{tr("From")}</label>
        <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        <label>{tr("To")}</label>
        <input type="date" value={to} min={from} max={todayStr()} onChange={(e) => setTo(e.target.value)} />
        <button className="hr-export-btn" onClick={handleExport} disabled={data.length === 0}>{tr("Export CSV")}</button>
      </div>

      {error && <div className="hr-error">{error}</div>}

      <div className="hr-table-wrap">
        {loading ? (
          <div className="hr-loading">{tr("Loading...")}</div>
        ) : data.length === 0 ? (
          <div className="hr-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <p>{tr("No overtime recorded in this period.")}</p>
          </div>
        ) : (
          <table className="hr-table">
            <thead>
              <tr>
                <th>{tr("Employee")}</th>
                <th>{tr("OT Days")}</th>
                <th>{tr("Total OT Hours")}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <React.Fragment key={row.user.id}>
                  <tr
                    className="hr-row-clickable"
                    onClick={() => toggleExpand(row.user.id)}
                    title={tr("Click to expand daily breakdown")}
                  >
                    <td>
                      <div className="hr-emp-name">{row.user.first_name} {row.user.last_name}</div>
                      <div className="hr-emp-email">{row.user.email}</div>
                    </td>
                    <td>{row.ot_days}</td>
                    <td>{formatSeconds(row.total_overtime_seconds)}</td>
                  </tr>
                  {expanded[row.user.id] && (
                    <tr className="hr-expand-row">
                      <td colSpan={3}>
                        <div className="hr-expand-inner">
                          <table className="hr-expand-table">
                            <thead>
                              <tr>
                                <th>{tr("Date")}</th>
                                <th>{tr("Worked")}</th>
                                <th>{tr("Overtime")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {row.entries.map((entry, i) => (
                                <tr key={i}>
                                  <td>{entry.date}</td>
                                  <td>{formatSeconds(entry.worked_seconds)}</td>
                                  <td>{formatSeconds(entry.overtime_seconds)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Tab: Payroll Export ───────────────────────────────────────────────────────

function PayrollTab({ auth }) {
  useTranslation();
  const [from, setFrom] = useState(thirtyDaysAgo());
  const [to, setTo] = useState(todayStr());
  const [presets, setPresets] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('datev_standard');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const authHeaders = useMemo(() => ({
    headers: { Authorization: `Bearer ${auth.token}`, 'Content-Type': 'application/json' },
  }), [auth.token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [presetRes, historyRes] = await Promise.all([
        axios.get(`/api/payroll-exports/presets?workspace_id=${auth.workspace_id}`, authHeaders),
        axios.get(`/api/payroll-exports/history?workspace_id=${auth.workspace_id}`, authHeaders),
      ]);
      const presetRows = presetRes.data.results || [];
      setPresets(presetRows);
      if (presetRows.length > 0 && !presetRows.find((p) => p.preset_key === selectedPreset)) {
        setSelectedPreset(presetRows[0].preset_key);
      }
      setHistory(historyRes.data.results || []);
    } catch (err) {
      setError(tr("Failed to load payroll export data."));
    } finally {
      setLoading(false);
    }
  }, [auth.workspace_id, authHeaders, selectedPreset]);

  const handleDownload = useCallback(async (jobId, fallbackName) => {
    try {
      setError('');
      const res = await axios.get(`/api/payroll-exports/file/${jobId}`, {
        ...authHeaders,
        params: { workspace_id: auth.workspace_id },
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] || 'text/csv' }));
      const fileNameHeader = res.headers['content-disposition'];
      const matchedName = fileNameHeader && /filename="?([^"]+)"?/i.exec(fileNameHeader);
      downloadFile(blobUrl, matchedName?.[1] || fallbackName || `payroll-export-${jobId}.csv`, () => {
        window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
      });
    } catch (err) {
      setError(err.response?.data?.errors?.[0]?.msg || tr("Failed to download payroll export."));
    }
  }, [auth.workspace_id, authHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleGenerate = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post('/api/payroll-exports/generate', JSON.stringify({
        workspace_id: auth.workspace_id,
        preset_key: selectedPreset,
        from,
        to,
      }), authHeaders);
      setSuccess(`Payroll export created: ${res.data.job.file_name}`);
      if (res.data.job?.id) {
        await handleDownload(res.data.job.id, res.data.job.file_name);
      }
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.errors?.[0]?.msg || tr("Failed to generate payroll export."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="hr-payroll-hero">
        <div>
          <h3>{tr("Payroll export history")}</h3>
          <p>{tr("Generate DATEV-friendly files and keep a history of every export sent to finance.")}</p>
        </div>
        <div className="hr-payroll-hero__meta">
          <span>{tr("DATEV CSV")}</span>
          <span>lexoffice</span>
          <span>sevDesk</span>
        </div>
      </div>

      <div className="hr-toolbar">
        <label>{tr("Preset")}</label>
        <select value={selectedPreset} onChange={(e) => setSelectedPreset(e.target.value)}>
          {presets.map((preset) => (
            <option key={preset.preset_key} value={preset.preset_key}>{preset.preset_name}</option>
          ))}
        </select>
        <label>{tr("From")}</label>
        <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        <label>{tr("To")}</label>
        <input type="date" value={to} min={from} max={todayStr()} onChange={(e) => setTo(e.target.value)} />
        <button className="hr-export-btn" onClick={handleGenerate} disabled={saving}>
          {saving ? tr("Generating...") : tr("Generate Export")}
        </button>
      </div>

      {error && <div className="hr-error">{error}</div>}
      {success && <div className="hr-success">{success}</div>}

      <div className="hr-history-grid">
        <div className="hr-history-card">
          <h4>{tr("Available presets")}</h4>
          {loading ? (
            <div className="hr-loading">{tr("Loading presets...")}</div>
          ) : (
            <div className="hr-preset-list">
              {presets.map((preset) => (
                <button
                  key={preset.preset_key}
                  className={`hr-preset-chip${selectedPreset === preset.preset_key ? ' hr-preset-chip--active' : ''}`}
                  onClick={() => setSelectedPreset(preset.preset_key)}
                >
                  {preset.preset_name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="hr-history-card">
          <h4>{tr("Recent exports")}</h4>
          {loading ? (
            <div className="hr-loading">{tr("Loading history...")}</div>
          ) : history.length === 0 ? (
            <div className="hr-empty">{tr("No payroll exports yet.")}</div>
          ) : (
            <div className="hr-export-history">
              {history.map((job) => (
                <div className="hr-export-item" key={job.id}>
                  <div>
                    <strong>{job.file_name}</strong>
                    <div>{job.preset_key} · {job.row_count} {tr("rows")}</div>
                  </div>
                  <div>
                    <span>{new Date(job.created_at).toLocaleDateString(getLocale(), { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    {job.id && (
                      <button
                        type="button"
                        className="hr-inline-link"
                        onClick={() => handleDownload(job.id, job.file_name)}
                      >{tr("Download")} </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function HRReportPage() {
  useTranslation();
  const auth         = useSelector((s) => s.auth);
  const isPrivileged = auth.is_workspace_admin === 1 || ['ADMIN', 'MANAGER'].includes(auth.workspace_role);
  const [tab, setTab] = useState(() => getTabFromHash(window.location.hash));

  useEffect(() => {
    const syncTabWithHash = () => {
      setTab(getTabFromHash(window.location.hash));
    };

    window.addEventListener('hashchange', syncTabWithHash);
    syncTabWithHash();

    return () => {
      window.removeEventListener('hashchange', syncTabWithHash);
    };
  }, []);

  const selectTab = useCallback((nextTab) => {
    const normalizedTab = HR_REPORT_TABS.includes(nextTab) ? nextTab : 'attendance';
    setTab(normalizedTab);
    const nextHash = `#${normalizedTab}`;
    if (window.location.hash !== nextHash) {
      window.history.replaceState(null, '', nextHash);
    }
  }, []);

  if (!isPrivileged) {
    return (
      <div>
        <Sidebar />
        <div className="hr-page">
          <div className="hr-empty" style={{ marginTop: '80px' }}>{tr("You don't have permission to view HR Reports.")} </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Sidebar />
      <div className="hr-page">
        <div className="hr-header">
          <div>
            <h1 className="hr-title">{tr("HR Reports")}</h1>
            <p className="hr-subtitle">{tr("Attendance, leave balances, and overtime for your workspace.")}</p>
          </div>
        </div>

        <div className="hr-tabs">
          <button
            className={`hr-tab${tab === 'attendance' ? ' hr-tab--active' : ''}`}
            onClick={() => selectTab('attendance')}
          >{tr("Attendance")} </button>
          <button
            className={`hr-tab${tab === 'leave' ? ' hr-tab--active' : ''}`}
            onClick={() => selectTab('leave')}
          >{tr("Leave")} </button>
          <button
            className={`hr-tab${tab === 'overtime' ? ' hr-tab--active' : ''}`}
            onClick={() => selectTab('overtime')}
          >{tr("Overtime")} </button>
          <button
            className={`hr-tab${tab === 'payroll' ? ' hr-tab--active' : ''}`}
            onClick={() => selectTab('payroll')}
          >{tr("Payroll Export")} </button>
        </div>

        {tab === 'attendance' && <AttendanceTab auth={auth} />}
        {tab === 'leave' && <LeaveTab auth={auth} />}
        {tab === 'overtime' && <OvertimeTab auth={auth} />}
        {tab === 'payroll' && <PayrollTab auth={auth} />}
      </div>
      <Footer />
    </div>
  );
}
